import { useEffect, useRef, useState } from "react";

/**
 * useStreamResilience — passive buffering indicator for live streams.
 *
 * For Icecast/Shoutcast, stalls and waiting events are NORMAL and frequent.
 * This hook only shows a buffering indicator — it does NOT auto-reconnect
 * because that causes audible skipping and breaks the Web Audio API pipeline.
 * The user can manually reconnect via the Refresh button in the header.
 */
export function useStreamResilience(audioRef, {
  enabled = true,
} = {}) {
  const [isBuffering, setIsBuffering] = useState(false);
  const bufferingTimerRef = useRef(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !enabled) return;

    const onTimeUpdate = () => {
      // Stream is progressing — clear any buffering state
      if (bufferingTimerRef.current) {
        clearTimeout(bufferingTimerRef.current);
        bufferingTimerRef.current = null;
      }
      setIsBuffering(false);
    };

    const onWaiting = () => {
      // Only show buffering after 2 seconds of waiting (skip brief stalls)
      if (!bufferingTimerRef.current) {
        bufferingTimerRef.current = setTimeout(() => {
          setIsBuffering(true);
        }, 2000);
      }
    };

    const onPlaying = () => {
      if (bufferingTimerRef.current) {
        clearTimeout(bufferingTimerRef.current);
        bufferingTimerRef.current = null;
      }
      setIsBuffering(false);
    };

    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("waiting", onWaiting);
    audio.addEventListener("playing", onPlaying);

    return () => {
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("waiting", onWaiting);
      audio.removeEventListener("playing", onPlaying);
      if (bufferingTimerRef.current) clearTimeout(bufferingTimerRef.current);
    };
  }, [audioRef, enabled]);

  return { isBuffering };
}
